import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSpaceById } from '@/api/spaces';
import { useCollectionById } from '@/features/collections/hooks/useCollections';
import { useDatasetById } from '@/features/datasets/hooks/useDatasets';

export interface Breadcrumb {
  name: string;
  link: string;
}
/**
 * Custom hook that generates breadcrumb navigation based on current route parameters.
 *
 * @description This hook analyzes the current URL parameters (spaceId, datasetId, collectionId)
 * and constructs a breadcrumb trail that reflects the hierarchical navigation structure.
 * It fetches dataset and collection data to display proper names in the breadcrumbs.
 *
 * @returns {Breadcrumb[]} An array of breadcrumb objects containing name and link properties
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const breadcrumbs = useBreadCrumbs();
 *
 *   return (
 *     <nav>
 *       {breadcrumbs.map((crumb, index) => (
 *         <Link key={index} to={crumb.link}>{crumb.name}</Link>
 *       ))}
 *     </nav>
 *   );
 * };
 * ```
 */
export const useBreadCrumbs = (): Breadcrumb[] => {
  const { spaceId, datasetId, collectionId } = useParams<{
    spaceId?: string;
    datasetId?: string;
    collectionId?: string;
  }>();
  const { data: space } = useSpaceById(spaceId);
  const { data: dataset } = useDatasetById(datasetId);
  const { data: collection } = useCollectionById(collectionId);

  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  useEffect(() => {
    const newBreadcrumbs: Breadcrumb[] = [];

    if (spaceId) {
      newBreadcrumbs.push({
        name: space?.name,
        link: `/dashboard/spaces/${spaceId}/datasets`,
      });
    }
    if (datasetId && dataset?.name) {
      newBreadcrumbs.push({
        name: dataset.name,
        link: `/dashboard/spaces/${spaceId}/datasets/${datasetId}/collections`,
      });
    }

    if (collectionId && collection?.name) {
      newBreadcrumbs.push({
        name: collection.name,
        link: `/dashboard/spaces/${spaceId}/datasets/${datasetId}/collections/${collectionId}`,
      });
    }

    if (newBreadcrumbs.length > 0) {
      setBreadcrumbs(newBreadcrumbs);
    }
  }, [spaceId, datasetId, collectionId, space, dataset, collection]);
  return breadcrumbs;
};
